import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountantClients, type AccountantClient } from '@/api/hooks/useAccountantClients';

type ClientSwitcherProps = {
  currentGrantId?: string;
  currentTaxYear?: number;
  onClientSwitch?: () => void;
};

function clientHref(client: AccountantClient): string {
  const year = client.tax_years[0]?.tax_year;
  return year === undefined ? '/accountant/invitation' : `/accountant/clients/${client.grant_id}/${year}/review`;
}

export function ClientSwitcher({ currentGrantId, currentTaxYear, onClientSwitch }: ClientSwitcherProps) {
  const navigate = useNavigate();
  const query = useAccountantClients();
  const [open, setOpen] = useState(false);
  const clients = useMemo(
    () => [...(query.data?.clients ?? [])].sort((left, right) => (right.tax_years[0]?.open_item_count ?? 0) - (left.tax_years[0]?.open_item_count ?? 0)),
    [query.data?.clients],
  );
  const label = query.data ? `Clients · ${query.data.clients.length}` : 'Clients';

  return (
    <div className="relative" data-testid="client-switcher">
      <button type="button" onClick={() => setOpen((value) => !value)} className="min-h-11 rounded-md border border-input bg-card px-3 text-sm font-semibold" aria-expanded={open}>{label}</button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-xl border bg-card shadow-lg" role="dialog" aria-label="Clients">
          <div className="border-b p-4"><h2 className="font-semibold">{label}</h2></div>
          {query.isPending && <p role="status" className="p-4 text-sm text-muted-foreground">Loading clients…</p>}
          {query.isError && <div role="alert" className="p-4 text-sm"><p>Clients could not be loaded.</p><button type="button" onClick={() => query.refetch()} className="mt-3 min-h-11 rounded-md border px-3">Retry</button></div>}
          {!query.isPending && !query.isError && clients.length === 0 && <p className="p-4 text-sm text-muted-foreground">No accepted clients yet.</p>}
          {!query.isPending && !query.isError && clients.map((client) => {
            const taxYear = client.tax_years[0];
            const current = client.grant_id === currentGrantId && taxYear?.tax_year === currentTaxYear;
            return (
              <button
                key={client.grant_id}
                type="button"
                className="flex min-h-14 w-full items-center justify-between gap-3 border-b px-4 py-2 text-left hover:bg-muted"
                aria-label={client.owner_display_name}
                onClick={() => {
                  onClientSwitch?.();
                  setOpen(false);
                  navigate(clientHref(client));
                }}
              >
                <span><span className="block font-semibold">{client.owner_display_name}</span><span className="block text-xs text-muted-foreground"><span>{client.entity_type ?? 'Business'}</span> · <span>{taxYear?.tax_year ?? 'Year unavailable'}</span> · <span>{taxYear?.open_item_count ?? '—'} open</span></span></span>
                <span className="shrink-0 text-xs text-muted-foreground">{current ? 'Current' : taxYear?.status === 'locked' ? 'Filed · locked' : 'In progress'}</span>
              </button>
            );
          })}
          <p className="p-4 text-xs text-muted-foreground">Switching clears filters, selections and unsent drafts on screen. Drafts already saved stay with their client.</p>
        </div>
      )}
    </div>
  );
}

export default ClientSwitcher;
